import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { useFlashMessage } from '../context/FlashMessageContext';
import { API_CONFIG } from '../config/api';
import { Shield, Monitor, Trash2, Check, Pencil } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import './TrustedDevices.css';
import './Dashboard.css';

function TrustedDevices() {
  const { user, token, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const { showSuccess, showError } = useFlashMessage();
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, inactive
  const [revokingId, setRevokingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const fetchDevices = useCallback(async () => {
    if (!token) return;
    setLoadingDevices(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await axios.get(
        `${API_CONFIG.BASE_URL}/user/trusted-devices${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDevices(response.data?.devices || []);
    } catch (error) {
      console.error('Failed to fetch trusted devices:', error);
      showError('Failed to load trusted devices');
      setDevices([]);
    } finally {
      setLoadingDevices(false);
    }
  }, [token, statusFilter, showError]);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const getDeviceLabel = (device) =>
    device.customName || device.device_name || device.operating_system || device.deviceFingerprint?.slice(0, 24) || 'Unknown Device';

  const handleSaveCustomName = async (device, customName) => {
    setEditingId(device.deviceFingerprint);
    try {
      await axios.patch(
        `${API_CONFIG.BASE_URL}/user/trusted-devices/custom-name`,
        {
          deviceFingerprint: device.deviceFingerprint,
          customName: customName?.trim() || null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      showSuccess(customName ? 'Device name updated' : 'Device name cleared');
      fetchDevices();
    } catch (error) {
      console.error('Failed to update custom name:', error);
      showError(error.response?.data?.message || 'Failed to update device name');
    } finally {
      setEditingId(null);
    }
  };

  const handleTogglePermanentTrust = async (device) => {
    const next = !device.permanentlyTrusted;
    setTogglingId(device.deviceFingerprint);
    try {
      await axios.patch(
        `${API_CONFIG.BASE_URL}/user/trusted-devices/permanent-trust`,
        {
          deviceFingerprint: device.deviceFingerprint,
          permanentlyTrusted: next,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      showSuccess(next ? 'Device permanently trusted' : 'Permanent trust removed');
      fetchDevices();
    } catch (error) {
      console.error('Failed to toggle permanent trust:', error);
      showError(error.response?.data?.message || 'Failed to update');
    } finally {
      setTogglingId(null);
    }
  };

  const handleRemoveDevice = async (device) => {
    if (!window.confirm(
      `Remove this device from trust? It will require MFA approval on next login.\n\nDevice: ${getDeviceLabel(device)}`
    )) {
      return;
    }

    setRevokingId(device.deviceFingerprint);
    try {
      await axios.post(
        `${API_CONFIG.BASE_URL}/user/trusted-devices/revoke`,
        { deviceFingerprint: device.deviceFingerprint },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      showSuccess('Device removed from trust');
      fetchDevices();
    } catch (error) {
      console.error('Failed to remove device:', error);
      showError(error.response?.data?.message || 'Failed to remove device');
    } finally {
      setRevokingId(null);
    }
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return 'Never';
    const d = new Date(isoStr);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatTrustExpiry = (device) => {
    if (device.permanentlyTrusted || !device.trustExpiresAt) return null;
    const expires = new Date(device.trustExpiresAt);
    if (expires.getTime() < Date.now()) return 'Trust expired';
    return `Trust ends: ${formatDate(device.trustExpiresAt)}`;
  };

  const truncateAgent = (agent, maxLen = 50) => {
    if (!agent) return 'Unknown';
    return agent.length > maxLen ? agent.slice(0, maxLen) + '...' : agent;
  };

  return (
    <DashboardLayout activeNav="trusted-devices">
        <div className="dashboard-section">
          <h2>
            <Shield size={24} />
            Trusted Devices
          </h2>
          <p className="trusted-devices-description">
            Devices that have been used for adaptive MFA. Active devices skip MFA within the trust window. Permanently trusted devices always skip MFA. Remove a device to require MFA on next login.
          </p>

          <div className="filter-tabs">
            <button
              className={`filter-tab ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All
            </button>
            <button
              className={`filter-tab ${statusFilter === 'active' ? 'active' : ''}`}
              onClick={() => setStatusFilter('active')}
            >
              Active
            </button>
            <button
              className={`filter-tab ${statusFilter === 'inactive' ? 'active' : ''}`}
              onClick={() => setStatusFilter('inactive')}
            >
              Inactive
            </button>
          </div>

          {loadingDevices ? (
            <div className="trusted-devices-loading">
              <div className="loading-spinner"></div>
              <p>Loading trusted devices...</p>
            </div>
          ) : devices.length === 0 ? (
            <div className="trusted-devices-empty">
              <Monitor size={48} />
              <p>No trusted devices found</p>
              <p className="empty-hint">
                Trusted devices appear here after you complete adaptive MFA from a device. Share credentials with a tenant and log in with adaptive MFA to build this list.
              </p>
            </div>
          ) : (
            <ul className="trusted-devices-list">
              {devices.map((device) => (
                <li key={device.deviceFingerprint} className="trusted-device-card">
                  <div className="device-info">
                    <div className="device-header">
                      <Monitor size={20} className="device-icon" />
                      {editingId === device.deviceFingerprint ? (
                        <input
                          type="text"
                          defaultValue={getDeviceLabel(device)}
                          maxLength={64}
                          onBlur={(e) => handleSaveCustomName(device, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveCustomName(device, e.target.value);
                            }
                          }}
                          className="device-label-edit"
                          style={{ padding: '2px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 'inherit', minWidth: 120 }}
                          autoFocus
                        />
                      ) : (
                        <span
                          className="device-label editable"
                          onClick={() => setEditingId(device.deviceFingerprint)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && setEditingId(device.deviceFingerprint)}
                        >
                          {getDeviceLabel(device)}
                          <Pencil size={14} style={{ marginLeft: 6, verticalAlign: 'middle', opacity: 0.6 }} />
                        </span>
                      )}
                      {device.isActive && (
                        <span className="active-badge">
                          <Check size={12} /> Active
                        </span>
                      )}
                      {!device.isActive && (
                        <span className="inactive-badge">Inactive</span>
                      )}
                      {device.permanentlyTrusted && (
                        <span className="permanent-trust-badge" style={{ marginLeft: 6, background: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: 9999, fontSize: 12 }}>
                          <Shield size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                          Permanently trusted
                        </span>
                      )}
                    </div>
                    <div className="device-details">
                      {device.ip_address && <span>IP: {device.ip_address}</span>}
                      {device.city && <span> • {device.city}</span>}
                      {device.tenantDisplayName && (
                        <span className="device-tenant"> • {device.tenantDisplayName}</span>
                      )}
                    </div>
                    {device.agent && (
                      <p className="device-agent" title={device.agent}>
                        {truncateAgent(device.agent)}
                      </p>
                    )}
                    <p className="device-last-used">
                      Last used: {formatDate(device.lastMfaAt)}
                      {formatTrustExpiry(device) && (
                        <span className={device.isActive ? '' : ' device-trust-expired'}>
                          {' • '}{formatTrustExpiry(device)}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="device-actions">
                    <label className="permanent-trust-toggle" style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 12 }}>
                      <input
                        type="checkbox"
                        checked={!!device.permanentlyTrusted}
                        onChange={() => handleTogglePermanentTrust(device)}
                        disabled={togglingId === device.deviceFingerprint}
                      />
                      <span>Permanently trust</span>
                    </label>
                    <button
                      className="remove-device-btn"
                      onClick={() => handleRemoveDevice(device)}
                      disabled={revokingId === device.deviceFingerprint}
                      title="Remove device from trust"
                    >
                      {revokingId === device.deviceFingerprint ? (
                        <span className="removing">Removing...</span>
                      ) : (
                        <>
                          <Trash2 size={16} /> Remove
                        </>
                      )}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
    </DashboardLayout>
  );
}

export default TrustedDevices;
